import { BaseService } from './base';
import { ApiRequest } from '../lib/request';
import type { ApiResponse } from '../lib/response';

import { Minisite, MinisiteImage } from '../models/minisite';

export type MinisiteSettingsGroup =
  | 'businessInfo'
  | 'minimiseGaps'
  | 'newCustomerDefaults'
  | 'paymentSettings'
  | 'preferences'
  | 'tabs'
  | 'terms'
  | 'themeSettings';

export class MinisiteService extends BaseService<Minisite> {
  override model = Minisite;
  override modelName = 'minisite';
  override modelLabel = 'Minisite';

  override get modelBaseUrl(): string {
    return `/api/v${this.modelApiVersion}/settings/${this.modelName}`;
  }

  async getMinisite(): Promise<ApiResponse<Minisite>> {
    const request = new ApiRequest({
      method: 'GET',
      url: this.modelBaseUrl,
    });

    return await this.requestItem(request, Minisite);
  }

  async saveMinisite(
    minisite: Minisite,
    groups: readonly [MinisiteSettingsGroup, ...MinisiteSettingsGroup[]],
  ): Promise<ApiResponse<Minisite>> {
    const settings = minisite.toJSON();
    const data = Object.fromEntries(
      groups.map(group => [group, settings[group]]),
    );

    const request = new ApiRequest({
      method: 'POST',
      url: this.modelBaseUrl,
      data,
    });

    return await this.requestItem(request, Minisite);
  }

  async getImage(
    imageType: 'miniSiteOnlineImage' | 'miniSiteGiftCardImage' | 'miniSitePassImage' | 'miniSiteGalleryImages',
  ): Promise<ApiResponse<MinisiteImage>> {
    const url = `/api/v${this.modelApiVersion}/settings/${imageType}`;

    const request = new ApiRequest({
      method: 'GET',
      url,
    });

    return await this.requestItem(request, MinisiteImage);
  }

  async uploadImage(
    imageType: 'miniSiteOnlineImage' | 'miniSiteGiftCardImage' | 'miniSitePassImage' | 'miniSiteGalleryImages',
    image: File,
    fieldType: string,
    onUploadProgress?: (progressEvent: any) => void,
  ): Promise<ApiResponse<MinisiteImage>> {
    const url = `/api/v${this.modelApiVersion}/settings/${imageType}`;

    const formData = new FormData();
    formData.append(fieldType, image);

    const request = new ApiRequest({
      method: 'POST',
      url,
      data: formData,
      onUploadProgress,
    });

    return await this.requestItem(request, MinisiteImage);
  }

  async deleteImage(
    imageType: 'miniSiteOnlineImage' | 'miniSiteGiftCardImage' | 'miniSitePassImage' | 'miniSiteGalleryImages',
    fieldType: string,
    image?: { id: number },
  ): Promise<ApiResponse<MinisiteImage>> {
    const url = `/api/v${this.modelApiVersion}/settings/${imageType}/${image?.id || 1}`;

    const request = new ApiRequest({
      method: 'DELETE',
      url,
      query: { type: fieldType },
    });

    return await this.requestItem(request, MinisiteImage);
  }
}
